import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { storage, db } from '../../lib/firebase';
import { User } from 'firebase/auth';

const AVATAR_CACHE_TTL = 5 * 60 * 1000; // 5分

interface AvatarCacheEntry {
  url: string | null;
  timestamp: number;
}

const avatarUrlCache = new Map<string, AvatarCacheEntry>();
const inflightAvatarRequests = new Map<string, Promise<string | null>>();

/**
 * ユーザーのアバターURLを取得する（キャッシュ＋リクエスト重複排除付き）
 * UserMiniBadge / MemberAvatar など複数箇所から呼び出される想定
 */
export const getCachedAvatarUrl = async (userId: string): Promise<string | null> => {
  const cached = avatarUrlCache.get(userId);
  if (cached && Date.now() - cached.timestamp < AVATAR_CACHE_TTL) {
    return cached.url;
  }

  const inflight = inflightAvatarRequests.get(userId);
  if (inflight) return inflight;

  const promise = (async (): Promise<string | null> => {
    try {
      const photoPath = await getUserImagePath(userId);
      if (photoPath) {
        const storageRef = ref(storage, photoPath);
        const url = await getDownloadURL(storageRef);
        avatarUrlCache.set(userId, { url, timestamp: Date.now() });
        return url;
      }
      avatarUrlCache.set(userId, { url: null, timestamp: Date.now() });
      return null;
    } catch {
      return null;
    } finally {
      inflightAvatarRequests.delete(userId);
    }
  })();

  inflightAvatarRequests.set(userId, promise);
  return promise;
};

export const invalidateAvatarCache = (userId: string) => {
  avatarUrlCache.delete(userId);
};

/**
 * ユーザー画像URLを取得する（Google/Apple認証対応）
 * @param user Firebase Auth User
 * @returns 画像URL（存在しない場合はnull）
 */
export const getUserImageURL = async (user: User): Promise<string | null> => {
  try {
    const cached = await getCachedAvatarUrl(user.uid);
    if (cached) return cached;

    if (user.photoURL) {
      return user.photoURL;
    }

    return null;
  } catch (error) {
    console.error('❌ [UserImageDebug] getUserImageURL error', error);
    return null;
  }
};

/**
 * ユーザー画像パスを取得する
 * @param userId ユーザーID
 * @returns 画像パス（存在しない場合はnull）
 */
export const getUserImagePath = async (userId: string): Promise<string | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (userDoc.exists()) {
      const userData = userDoc.data();
      const photoPath = userData.photoPath || null;
      return photoPath;
    }
    return null;
  } catch (error) {
    console.error('❌ [UserImageDebug] getUserImagePath error', { userId, error });
    return null;
  }
};
